import React, { useEffect, useRef, useState, useCallback } from "react";
import { telecmiApi } from "../lib/api";
import { useLiveCall } from "../contexts/LiveCallContext";
import {
  Phone,
  PhoneOff,
  Pause,
  Play,
  Mic,
  MicOff,
  Forward,
  Sparkles,
  Maximize2,
  Minimize2,
  GripVertical,
  Loader2,
  AlertTriangle,
  X,
} from "lucide-react";
import { toast } from "sonner";

/**
 * LiveCallWidget — global floating call console.
 *
 * Lives at right-center of the viewport, drag-to-reposition (saved in
 * localStorage). Polls the live session every 3s. Shows:
 *   - Customer name + duration timer
 *   - Mute / Hold / Transfer / End controls
 *   - Tabbed transcript + coaching feeds
 *
 * Mounts only when LiveCallContext.activeCall is set (i.e. the agent has
 * picked up a call OR a mock session is running).
 */
const STORAGE_KEY = "live-call-widget-pos";
const COLLAPSED_W = 360;
const EXPANDED_W = 480;
const HEIGHT = 560;

const fmtDuration = (sec) => {
  if (!sec || sec < 1) return "00:00";
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const URGENCY_COLOR = {
  high: { bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: "text-red-600" },
  medium: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", icon: "text-amber-600" },
  low: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800", icon: "text-blue-600" },
};

const LiveCallWidget = () => {
  const { activeCall, endCall } = useLiveCall();
  const [session, setSession] = useState(null);
  const [tab, setTab] = useState("transcript"); // transcript | coaching
  const [expanded, setExpanded] = useState(true);
  const [endingCall, setEndingCall] = useState(false);

  // --- Drag state ---
  const initial = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
  })();
  const [pos, setPos] = useState(initial || {
    right: 24,
    top: typeof window !== "undefined" ? Math.max(80, window.innerHeight / 2 - HEIGHT / 2) : 100,
  });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, startPos: null });

  const onMouseDown = useCallback((e) => {
    dragRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      startPos: { ...pos },
    };
    e.preventDefault();
  }, [pos]);

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d.dragging) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      // We anchor by `right` so subtract dx
      const w = expanded ? EXPANDED_W : COLLAPSED_W;
      const newRight = Math.max(8, d.startPos.right - dx);
      const newTop = Math.max(8, Math.min(window.innerHeight - 80, d.startPos.top + dy));
      // Prevent sliding off the left
      const newRightCapped = Math.min(window.innerWidth - w - 8, newRight);
      setPos({ right: newRightCapped, top: newTop });
    };
    const onUp = () => {
      if (!dragRef.current.dragging) return;
      dragRef.current.dragging = false;
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pos)); } catch {}
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [pos, expanded]);

  // --- Polling for live session state ---
  useEffect(() => {
    if (!activeCall?.id) return;
    let mounted = true;
    let coachTimer = null;
    const fetchOnce = async () => {
      try {
        const r = await telecmiApi.liveGet(activeCall.id);
        if (mounted) setSession(r.data);
      } catch (e) {
        // 404 → call ended; clear local state
        if (e?.response?.status === 404) {
          endCall();
        }
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 3000);
    // Coaching: every 20s
    const coachLoop = async () => {
      try { await telecmiApi.liveCoach(activeCall.id); } catch {}
    };
    coachTimer = setInterval(coachLoop, 20_000);
    // Trigger first coach after 8s so the agent sees something quickly
    const firstCoach = setTimeout(coachLoop, 8000);
    return () => {
      mounted = false;
      clearInterval(t);
      clearInterval(coachTimer);
      clearTimeout(firstCoach);
    };
  }, [activeCall?.id, endCall]);

  // --- Auto-scroll feeds ---
  const transcriptEndRef = useRef(null);
  const coachingEndRef = useRef(null);
  useEffect(() => {
    if (tab === "transcript") transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
    else coachingEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [session?.transcript?.length, session?.coaching?.length, tab]);

  // --- Duration timer ---
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!activeCall?.id) return null;

  const startedAt = session?.started_at ? new Date(session.started_at).getTime() : Date.now();
  const seconds = Math.floor((now - startedAt) / 1000);

  // --- Controls ---
  const handleToggleMute = async () => {
    if (!session) return;
    try {
      const r = session.muted ? await telecmiApi.liveUnmute(session.id) : await telecmiApi.liveMute(session.id);
      setSession((s) => ({ ...s, muted: !s.muted }));
      toast.success(r.data?.muted ? "Muted" : "Unmuted");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not toggle mute"); }
  };
  const handleToggleHold = async () => {
    if (!session) return;
    try {
      const r = session.on_hold ? await telecmiApi.liveResume(session.id) : await telecmiApi.liveHold(session.id);
      setSession((s) => ({ ...s, on_hold: !s.on_hold }));
      toast.success(r.data?.on_hold ? "On hold" : "Resumed");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not toggle hold"); }
  };
  const handleTransfer = async () => {
    const ext = window.prompt("Transfer to extension (e.g. 1002)");
    if (!ext) return;
    const mode = window.confirm("OK = warm transfer (3-way bridge first)\nCancel = cold transfer (immediate handoff)") ? "warm" : "cold";
    try {
      await telecmiApi.liveTransfer(session.id, { to_extension: ext.trim(), mode });
      toast.success(`${mode === "warm" ? "Warm" : "Cold"} transfer to ext ${ext.trim()} started`);
    } catch (e) { toast.error(e.response?.data?.detail || "Transfer failed"); }
  };
  const handleEnd = async () => {
    if (!session) return;
    if (!window.confirm("End this call?")) return;
    setEndingCall(true);
    try {
      await telecmiApi.liveEnd(session.id);
      toast.success("Call ended");
      endCall();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not end call");
      setEndingCall(false);
    }
  };

  const w = expanded ? EXPANDED_W : COLLAPSED_W;
  const transcript = session?.transcript || [];
  const coaching = session?.coaching || [];
  const newestCoach = coaching[coaching.length - 1];

  return (
    <div
      className="fixed z-[150] select-none"
      style={{ right: pos.right, top: pos.top, width: w, height: HEIGHT }}
      data-testid="live-call-widget"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col h-full overflow-hidden">
        {/* Drag handle / header */}
        <div
          onMouseDown={onMouseDown}
          className="cursor-move bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-3 py-2 flex items-center gap-2"
          data-testid="live-call-drag-handle"
        >
          <GripVertical className="w-4 h-4 opacity-70" />
          <Phone className="w-4 h-4 animate-pulse" />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-extrabold truncate">{activeCall.name || activeCall.phone}</div>
            <div className="text-[10px] opacity-90 font-mono">
              {activeCall.phone} · {fmtDuration(seconds)}{session?.is_mock ? " · MOCK" : ""}
            </div>
          </div>
          <button
            onClick={() => setExpanded((x) => !x)}
            title={expanded ? "Collapse" : "Expand"}
            className="text-white/90 hover:text-white p-1"
            data-testid="live-call-toggle-expand"
          >
            {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => endCall()}
            title="Hide widget (call stays active)"
            className="text-white/90 hover:text-white p-1"
            data-testid="live-call-hide-btn"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Latest coaching pill — always visible */}
        {newestCoach && (
          <div
            className={`mx-2 mt-2 rounded-lg p-2 border ${URGENCY_COLOR[newestCoach.urgency]?.bg || "bg-blue-50"} ${URGENCY_COLOR[newestCoach.urgency]?.border || "border-blue-200"} flex items-start gap-2`}
            data-testid="live-call-latest-coach"
          >
            <Sparkles className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${URGENCY_COLOR[newestCoach.urgency]?.icon || "text-blue-600"}`} />
            <div className="flex-1 min-w-0">
              <div className={`text-[11px] font-bold ${URGENCY_COLOR[newestCoach.urgency]?.text || "text-blue-800"}`}>{newestCoach.tip}</div>
              <div className="text-[9px] uppercase tracking-wider mt-0.5 text-gray-500">
                {newestCoach.category} · {newestCoach.urgency} urgency
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        {expanded && (
          <div className="flex items-center gap-1 px-2 pt-2 border-b border-gray-100">
            <button
              onClick={() => setTab("transcript")}
              className={`text-[11px] font-bold px-3 py-1.5 rounded-t-md ${tab === "transcript" ? "bg-gray-100 text-emerald-700" : "text-gray-500 hover:text-gray-900"}`}
              data-testid="live-call-tab-transcript"
            >
              Transcript {transcript.length > 0 && <span className="text-[9px] ml-1 bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">{transcript.length}</span>}
            </button>
            <button
              onClick={() => setTab("coaching")}
              className={`text-[11px] font-bold px-3 py-1.5 rounded-t-md ${tab === "coaching" ? "bg-gray-100 text-purple-700" : "text-gray-500 hover:text-gray-900"}`}
              data-testid="live-call-tab-coaching"
            >
              AI Coaching {coaching.length > 0 && <span className="text-[9px] ml-1 bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">{coaching.length}</span>}
            </button>
          </div>
        )}

        {/* Body */}
        {expanded && (
          <div className="flex-1 overflow-auto px-3 py-2 bg-gray-50/40">
            {tab === "transcript" ? (
              transcript.length === 0 ? (
                <div className="text-[11px] text-gray-400 py-6 text-center" data-testid="live-call-transcript-empty">
                  Listening… transcript appears live.
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="live-call-transcript">
                  {transcript.map((u, i) => (
                    <div key={`${u.ts}-${i}`} className={`flex ${u.speaker === "agent" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] text-[11px] rounded-lg px-2 py-1 ${
                        u.speaker === "agent" ? "bg-emerald-100 text-emerald-900" :
                        u.speaker === "system" ? "bg-amber-50 text-amber-900 italic" :
                        "bg-white border border-gray-200"
                      }`}>
                        <div className="text-[9px] uppercase font-bold opacity-70 mb-0.5">{u.speaker}</div>
                        {u.text}
                      </div>
                    </div>
                  ))}
                  <div ref={transcriptEndRef} />
                </div>
              )
            ) : (
              coaching.length === 0 ? (
                <div className="text-[11px] text-gray-400 py-6 text-center" data-testid="live-call-coaching-empty">
                  Claude is listening… first nudge in a few seconds.
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="live-call-coaching">
                  {coaching.map((c, i) => {
                    const cc = URGENCY_COLOR[c.urgency] || URGENCY_COLOR.low;
                    return (
                      <div key={`${c.ts}-${i}`} className={`text-[11px] ${cc.bg} border ${cc.border} ${cc.text} rounded-lg p-2`}>
                        <div className="flex items-start gap-1.5">
                          <Sparkles className={`w-3 h-3 mt-0.5 shrink-0 ${cc.icon}`} />
                          <div className="flex-1 min-w-0">
                            <div className="font-bold">{c.tip}</div>
                            <div className="text-[9px] uppercase tracking-wider mt-0.5 text-gray-500">{c.category} · {c.urgency}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={coachingEndRef} />
                </div>
              )
            )}
          </div>
        )}

        {/* Controls */}
        <div className="border-t border-gray-200 bg-white px-2 py-2 flex items-center justify-between gap-1">
          <button
            onClick={handleToggleMute}
            className={`flex-1 flex items-center justify-center gap-1 text-[11px] font-bold py-1.5 rounded-lg border transition ${
              session?.muted ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
            }`}
            title={session?.muted ? "Unmute" : "Mute"}
            data-testid="live-call-mute-btn"
          >
            {session?.muted ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
            {session?.muted ? "Unmute" : "Mute"}
          </button>
          <button
            onClick={handleToggleHold}
            className={`flex-1 flex items-center justify-center gap-1 text-[11px] font-bold py-1.5 rounded-lg border transition ${
              session?.on_hold ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
            }`}
            title={session?.on_hold ? "Resume" : "Hold"}
            data-testid="live-call-hold-btn"
          >
            {session?.on_hold ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            {session?.on_hold ? "Resume" : "Hold"}
          </button>
          <button
            onClick={handleTransfer}
            className="flex-1 flex items-center justify-center gap-1 text-[11px] font-bold py-1.5 rounded-lg border bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
            title="Transfer (cold or warm)"
            data-testid="live-call-transfer-btn"
          >
            <Forward className="w-3.5 h-3.5" /> Transfer
          </button>
          <button
            onClick={handleEnd}
            disabled={endingCall}
            className="flex-1 flex items-center justify-center gap-1 text-[11px] font-bold py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            data-testid="live-call-end-btn"
          >
            {endingCall ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PhoneOff className="w-3.5 h-3.5" />}
            End
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveCallWidget;
